// What a thin answer embed puts on screen, derived from an answer and nothing
// else. PostEmbed owns the API side — the cached answer, the hydration queue,
// measurement and the window geometry — and hands the result here, so the
// display can be reasoned about (and spec'd) without a store, a panel or a
// network round trip.

export type ThinEmbedMediaKind = 'image' | 'video' | 'audio' | 'file' | 'link' | null

export type ThinEmbedPresentation = {
    /** True when the viewer may not read the embedded answer at all. */
    isPrivate: boolean
    authorName: string | null
    /** Set only for a station/system byline that overrides the real author. */
    authorIconUrl: string | null
    /** The real author, for the avatar's own resolution; null when overridden. */
    author: any | null
    /** One line of the answer's text, already collapsed and trimmed. */
    snippet: string
    /** The single glyph that stands in for whatever media the answer carries. */
    mediaKind: ThinEmbedMediaKind
    /** A quote of a quote has no text of its own to show. */
    isRecursiveQuote: boolean
}

const PRIVATE_ANSWER_ID = '__PRIVATE__'
const SNIPPET_MAX_LENGTH = 140

const isFilledObject = (value: unknown): boolean =>
    Boolean(value) && typeof value === 'object' && Object.keys(value as object).length > 0

// Quill deltas: text lives in the string inserts, and every other insert is an
// embed (image, mention, divider) that contributes nothing readable.
export function deltaPlainText(delta: unknown): string {
    const ops = (delta as { ops?: unknown })?.ops
    if (!Array.isArray(ops)) return ''

    return ops
        .map(op => {
            const insert = (op as { insert?: unknown })?.insert
            return typeof insert === 'string' ? insert : ''
        })
        .join('')
}

// One line, whitespace collapsed, cut on a word boundary where there is one so
// the row never ends mid-word.
export function thinEmbedSnippet(text: string, maxLength = SNIPPET_MAX_LENGTH): string {
    const collapsed = String(text || '')
        .replace(/\s+/g, ' ')
        .trim()
    if (collapsed.length <= maxLength) return collapsed

    const clipped = collapsed.slice(0, maxLength)
    const lastSpace = clipped.lastIndexOf(' ')
    const cut = lastSpace > maxLength * 0.6 ? clipped.slice(0, lastSpace) : clipped
    return `${cut.trimEnd()}…`
}

// The one glyph that stands in for the answer's media. Ordered by what a reader
// would call the post: a video post with a thumbnail is still a video.
export function thinEmbedMediaKind(answer: any): ThinEmbedMediaKind {
    if (!answer) return null

    if (answer.recording_url) {
        const recordingType = String(
            answer.original_recording_type || answer.recording_type || '',
        ).toLowerCase()
        return recordingType.startsWith('audio/') ? 'audio' : 'video'
    }

    const files: any[] = Array.isArray(answer.file_urls) ? answer.file_urls : []
    const fileKind = (file: any): ThinEmbedMediaKind => {
        const contentType = String(file?.content_type || '').toLowerCase()
        if (contentType.startsWith('image/')) return 'image'
        if (contentType.startsWith('video/')) return 'video'
        if (contentType.startsWith('audio/')) return 'audio'
        return 'file'
    }
    // A post carrying several kinds is named by the richest one present.
    const priority: ThinEmbedMediaKind[] = ['video', 'audio', 'image', 'file']
    const kinds = new Set(files.map(fileKind))
    const richest = priority.find(kind => kinds.has(kind))
    if (richest) return richest

    if (answer.answer_image_url || answer.local_image_urls?.length) return 'image'
    if (answer.url) return 'link'
    return null
}

/**
 * Everything a thin embed renders, and nothing about how it got there.
 *
 * `answer` is the resolved (cached or hydrating) answer; `fallbackAnswer` is
 * the lean copy the container was handed, used only where the resolved one has
 * not filled a field in yet.
 */
export function thinEmbedPresentation(answer: any, fallbackAnswer?: any): ThinEmbedPresentation {
    const source = answer || fallbackAnswer || null
    const isPrivate = !source || source.id === PRIVATE_ANSWER_ID

    if (isPrivate) {
        return {
            isPrivate: true,
            authorName: null,
            authorIconUrl: null,
            author: null,
            snippet: '',
            mediaKind: null,
            isRecursiveQuote: false,
        }
    }

    // A quote of a quote carries its text one level down; the full embed reads
    // it the same way.
    const hasOwnDelta = isFilledObject(source.delta_json)
    const delta = hasOwnDelta ? source.delta_json : source.nested_delta_json
    const text = deltaPlainText(delta) || source.content || ''

    const author = source.user || fallbackAnswer?.user || null

    return {
        isPrivate: false,
        authorName: source.virtual_user_name || author?.name || null,
        authorIconUrl: source.virtual_user_icon_url || null,
        author: source.virtual_user_icon_url ? null : author,
        snippet: thinEmbedSnippet(text),
        mediaKind: thinEmbedMediaKind(source),
        isRecursiveQuote: !hasOwnDelta && isFilledObject(source.nested_delta_json),
    }
}
